import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
// PlansPage.tsx — Co Marca (igual que CategoryPage, sin franja ni stats desglosados)
import Loading from '../../../components/Loading';
import React, { useEffect, useState } from 'react';
import { Edit, Trash2, History, Plus, Notebook, ChevronRight, Home, CheckCircle, XCircle } from 'lucide-react';
import { fetchPlanes, Plan } from '../../../services/planService';
import AddPlanModal from '../../../components/Plan/AddPlanModal';
import Pagination from '../../../components/Pagination/Pagination';
import EditPlanModal from '../../../components/Plan/EditPlanModal';
import DeletePlanModal from '../../../components/Plan/DeletePlanModal';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const C = { primary:'var(--ad-primary, #3564db)',primaryDark:'var(--ad-primary-dark, #8f1f64)',secondary:'var(--ad-secondary, #3564db)',secondaryDark:'#004d7a',secondaryLight:'var(--ad-secondary, #92b1ed)',accentDark:'#ea8406' };

const css = `

.pp-wrap * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes ppFadeUp  { from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);} }
@keyframes ppBlob    { 0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%;}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%;} }
@keyframes spin { to{transform:rotate(360deg);} }
.pp-fadeup   { animation:ppFadeUp .45s cubic-bezier(.16,1,.3,1) both; }
.pp-fadeup-1 { animation:ppFadeUp .45s .08s cubic-bezier(.16,1,.3,1) both; }
.pp-card { background:var(--ad-surface, #fff);border-radius:20px;border:1.5px solid var(--ad-line, #f1f5f9);box-shadow:0 2px 12px rgba(0,0,0,.04);overflow:hidden; }
.pp-table-row { transition:background .15s; }
.pp-table-row:hover { background:var(--ad-soft, #f8fafc); }
.pp-action-btn { width:30px;height:30px;border-radius:9px;border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s; }
.pp-action-btn.edit { background:color-mix(in srgb, ${C.secondary} 6.27%, transparent);color:var(--ad-link, #2854c5); }
.pp-action-btn.edit:hover { background:color-mix(in srgb, ${C.secondary} 12.55%, transparent);transform:translateY(-1px); }
.pp-action-btn.del  { background:color-mix(in srgb, ${C.primary} 6.27%, transparent);color:var(--ad-link, #2854c5); }
.pp-action-btn.del:hover  { background:color-mix(in srgb, ${C.primary} 12.55%, transparent);transform:translateY(-1px); }
.pp-scroll::-webkit-scrollbar{width:5px;height:5px;}
.pp-scroll::-webkit-scrollbar-track{background:transparent;}
.pp-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.25);border-radius:10px;}
`;

const ExpandableComment = ({ comment }: { comment?: string|null }) => {
    const [expanded, setExpanded] = useState(false);
    const words = (comment||'').split(' ');
    const truncated = words.slice(0,5).join(' ')+(words.length>5?'…':'');
    if (!comment) return <span style={{color:'#cbd5e1',fontStyle:'italic',fontSize:'.8rem'}}>Sin comentarios</span>;
    return(
        <span onClick={()=>words.length>5&&setExpanded(!expanded)} style={{cursor:words.length>5?'pointer':'default',fontSize:'.82rem',color:'var(--ad-muted, #64748b)'}}
            onMouseEnter={e=>words.length>5&&((e.currentTarget as HTMLSpanElement).style.color=C.secondary)}
            onMouseLeave={e=>((e.currentTarget as HTMLSpanElement).style.color='var(--ad-muted, #64748b)')}>
            {expanded?comment:truncated}
        </span>
    );
};

const PlansPage: React.FC = () => {
    const [planes,setPlanes] = useState<Plan[]>([]);
    const [isModalOpen,setIsModalOpen] = useState(false);
    const [currentPage,setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [isEditModalOpen,setIsEditModalOpen] = useState(false);
    const [selectedPlan,setSelectedPlan] = useState<Plan|null>(null);
    const [isDeleteModalOpen,setIsDeleteModalOpen] = useState(false);
    const [planToDelete,setPlanToDelete] = useState<Plan|null>(null);
    const navigate = useNavigate();
    const { isSessionExpired,setIsSessionExpired } = useSessionExpiration();
    const { logout,user } = useAuth();
    const userLevelId = user?.user_level_id??0;
    const perm = useModelPermission('plan',userLevelId);

    useEffect(()=>{ const load=async()=>{ try{ if(perm?.can_read){const d=await fetchPlanes();setPlanes(d.sort((a,b)=>a.PlanID-b.PlanID));}else setPlanes([]); }catch{setPlanes([]);} }; load(); },[perm]);

    const handlePlanCreated=(p:Plan)=>setPlanes(prev=>[...prev,p]);
    const handlePlanUpdated=(p:Plan)=>setPlanes(prev=>prev.map(x=>x.PlanID===p.PlanID?p:x));
    const handlePlanDeleted=(id:number)=>setPlanes(prev=>prev.filter(p=>p.PlanID!==id));
    const handleDeleteClick=(p:Plan)=>{setPlanToDelete(p);setIsDeleteModalOpen(true);};

    const totalPages=Math.ceil(planes.length/itemsPerPage);
    const startIndex=(currentPage-1)*itemsPerPage;
    const displayedPlanes=planes.slice(startIndex,startIndex+itemsPerPage);

    if (perm === null) return <Loading text="Cargando planes..." />;

    return <><LumaResourceWorkspace title="Planes" description="Lista completa de planes" group="Planes" rows={planes} columns={[{key:'NombrePlan',label:'Nombre'},{key:'Comentarios',label:'Comentarios'},{key:'estado',label:'Estado'}]} idKey="PlanID" nameKey="NombrePlan" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsModalOpen(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedPlan(r);setIsEditModalOpen(true);}:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined} history="/plans/history"/><AddPlanModal isOpen={isModalOpen} onClose={()=>setIsModalOpen(false)} onCreated={handlePlanCreated}/><EditPlanModal isOpen={isEditModalOpen} onClose={()=>{setIsEditModalOpen(false);setSelectedPlan(null);}} initialData={selectedPlan} onUpdate={handlePlanUpdated}/><DeletePlanModal isOpen={isDeleteModalOpen} onClose={()=>setIsDeleteModalOpen(false)} planId={planToDelete?.PlanID??null} planName={planToDelete?.NombrePlan??''} onDeleted={handlePlanDeleted}/><ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{setIsSessionExpired(false);await logout();}}/></>;
};

export default PlansPage;
